import { useState, useEffect, useCallback, useContext } from 'react'
import { DashboardInfoResume } from './DashboardInfoResume'
import { Col, Row } from 'react-bootstrap'
import { TableCard } from '../../components/organisms/TableCard'
import WelcomeCardToCreateCompany from '../../components/organisms/WelcomeCardToCreateCompany'
import * as S from './styles'
import { PendenciesCard } from '../../components/organisms/PendenciesCard'
import { getDashboard } from '../../domain/usecases/Dashboard'
import { getCompany } from '../../domain/usecases/Company'
import { IGetSpes as getSpes } from '../../domain/usecases/SPE'
import { getProject } from '../../domain/usecases/Project'
import { getUnits } from '../../domain/usecases/Unit'
import {
  Contract,
  ContractStatuses,
  LastTenContracts
} from '../../domain/models/ContractsModel'
import { IndividualPersonModel } from '../../domain/models/IndividualPersonModel'
import { JuridicPersonModel } from '../../domain/models/JuridicPersonModel'
import { Pending } from '../../domain/models/DashboardModel'
import Information from '../../components/atoms/Information'
import { convertNumberToCurrencyFormat } from '../../utils/convertNumberToCurrencyFormat'
import { AppContext } from '../../app'
import { LoadingIndicator } from '../../components/atoms/LoadingIndicator'

interface DashboardProps {
  remoteGetDashboard: getDashboard
  remoteGetCompanies: getCompany
  remoteGetSpes: getSpes
  remoteGetProjects: getProject
  remoteGetUnits: getUnits
}

export interface IDashboardFirstSteps {
  companies: number
  spes: number
  projects: number
  units: number
}

const Dashboard = ({
  remoteGetDashboard,
  remoteGetCompanies,
  remoteGetSpes,
  remoteGetProjects,
  remoteGetUnits
}: DashboardProps) => {
  const disabled = true
  const { user } = useContext(AppContext)

  const [dashboardFirstSteps, setDashboardFirstSteps] =
    useState<IDashboardFirstSteps>({} as IDashboardFirstSteps)
  const [firstStepsCompleted, setFirstStepsCompleted] = useState(false)
  const [lastTenContracts, setLastTenContracts] = useState<LastTenContracts[]>(
    []
  )
  const [pendencies, setPendencies] = useState<Pending | null>(null)
  const [dashboardData, setDashboardData] = useState<any>(null)
  const [loading, setLoading] = useState(true)

  const getData = useCallback(async () => {
    console.log(
      '[DASHBOARD] getData called, user context:',
      user?.selectedContextEncoded ? 'PRESENT' : 'MISSING'
    )

    // Only fetch data if user has a selected context
    if (!user?.selectedContextEncoded) {
      console.log('[DASHBOARD] Waiting for context to be available...')
      return
    }

    try {
      // Load all data in parallel to avoid multiple sequential requests
      const [companies, spes, projects, units, dashboard] = await Promise.all([
        remoteGetCompanies.getAll(),
        remoteGetSpes.getAll(),
        remoteGetProjects.getProjects(),
        remoteGetUnits.getUnits(),
        remoteGetDashboard.getIndex()
      ])

      setDashboardFirstSteps({
        companies: companies.length ? 1 : 0,
        spes: spes.length ? 1 : 0,
        projects: projects.length ? 1 : 0,
        units: units.length ? 1 : 0
      })
      setDashboardData(dashboard)
      setLastTenContracts(dashboard.last_ten_contracts)
      setPendencies(dashboard.pending)
    } catch (error) {
      console.error('[DASHBOARD] Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }, [
    user?.selectedContextEncoded,
    remoteGetCompanies,
    remoteGetSpes,
    remoteGetProjects,
    remoteGetUnits,
    remoteGetDashboard
  ])

  useEffect(() => {
    getData()
  }, [getData])

  useEffect(() => {
    setFirstStepsCompleted(
      Object.values(dashboardFirstSteps).every((value) => value > 0)
    )
  }, [dashboardFirstSteps])

  // 'cancelled' existe no backend mas não no enum do front: a coluna ficava vazia
  const translateContractStatus = (status: ContractStatuses | 'cancelled') => {
    switch (status) {
      case 'cancelled':
        return 'Cancelado'
      case ContractStatuses.REGISTERED:
        return 'Registrado'
      case ContractStatuses.ERROR:
        return 'Inválido'
      case ContractStatuses.PASSWORD_PENDING:
        return 'Aguardando liberação de processamento'
      case ContractStatuses.PROCESSING:
        return 'Processando'
      case ContractStatuses.SIGNATURES_PENDING:
        return 'Assinatura(s) Pendente(s)'
    }
  }

  // Show loading indicator while waiting for context
  console.log(
    '[DASHBOARD RENDER] user?.selectedContextEncoded:',
    user?.selectedContextEncoded ? 'PRESENT' : 'MISSING',
    'loading:',
    loading
  )

  if (!user?.selectedContextEncoded || loading) {
    console.log('[DASHBOARD RENDER] Showing loading state')
    return (
      <S.Container disabled={disabled}>
        <Row>
          <Col className="text-center" style={{ padding: '50px' }}>
            <LoadingIndicator />
            <p style={{ marginTop: '20px' }}>Carregando dashboard...</p>
          </Col>
        </Row>
      </S.Container>
    )
  }

  console.log('[DASHBOARD RENDER] Rendering main content')

  return (
    <S.Container disabled={disabled}>
      <Row>
        <Col>
          {!firstStepsCompleted && (
            <WelcomeCardToCreateCompany data={dashboardFirstSteps} />
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <DashboardInfoResume
            disabled={disabled}
            remoteGetDashboard={remoteGetDashboard}
            dashboard={dashboardData}
          />
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <TableCard
            data={{
              titleHeader: 'Últimos negócios',
              thead: (
                <thead>
                  <tr>
                    <th>DATA</th>
                    <th>ID</th>
                    <th>TIPO</th>
                    <th>PARTES</th>
                    <th>VALOR (R$)</th>
                    <th>STATUS</th>
                  </tr>
                </thead>
              ),
              infoMsg: 'Seus últimos Contratos serão exibidos aqui.',
              tbody: (
                <tbody>
                  {!lastTenContracts.length ? (
                    <tr>
                      <td colSpan={6}>
                        <Information style={{ margin: '0' }} />
                      </td>
                    </tr>
                  ) : (
                    lastTenContracts.map((contract) => (
                      <tr key={contract.id}>
                        <td>{contract.created_at}</td>
                        <td>{contract.id}</td>
                        <td>Compra e Venda</td>
                        <td>{contract.buyers_name}</td>
                        <td>
                          {convertNumberToCurrencyFormat(
                            Number(contract.value)
                          )}
                        </td>
                        <td>{translateContractStatus(contract.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              )
            }}
          />
          {pendencies && <PendenciesCard pendencies={pendencies} />}
        </Col>
      </Row>
    </S.Container>
  )
}
export default Dashboard
